import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAnalyticsQuery, shiftRangeBackwards, shiftYearsBack, shiftMonthsBack } from './useAnalyticsQuery';
import { computePreset } from './DateRangeControl';
import AnalyticsFilters from './AnalyticsFilters';
import AnalyticsKpiStrip from './AnalyticsKpiStrip';
import AnalyticsChart from './AnalyticsChart';
import AnalyticsTable from './AnalyticsTable';
import AnalyticsDrilldownPanel from './AnalyticsDrilldownPanel';
import './analytics-pro.css';

/**
 * /v3/analytics — Pro Analytics page (Sprint 3.7).
 *
 * Filters live in URL params so links are sharable. Wraps the legacy
 * /api/analytics/* endpoints via v3Api so session-expiry is uniform.
 */

const CHART_TYPES = [
    { key: 'bar',   label: 'Bar' },
    { key: 'line',  label: 'Line' },
    { key: 'area',  label: 'Area' },
    { key: 'pie',   label: 'Pie' },
];

const GROUP_BY_OPTIONS = [
    { value: 'date',         label: 'Date' },
    { value: 'tenant',       label: 'Tenant' },
    { value: 'tenantgroup',  label: 'Tenant Group' },
    { value: 'property',     label: 'Property' },
    { value: 'category',     label: 'Category' },
    { value: 'subcategory',  label: 'Subcategory' },
    { value: 'floor',        label: 'Floor' },
];

const DEFAULT_METRICS = { sales: 1, transCount: 1, atv: 1, fb: 0, revenueShareAmount: 0, revenueSharePercentage: 0, baseRent: 0 };

export default function AnalyticsPage() {
    const { t } = useTranslation();
    const { concessionaireId, propertyAccess } = useAuth();

    // Read URL once at mount; we don't subscribe to React Router updates
    // because we write the URL via history.replaceState (see below).
    const initialParams = useMemo(
        () => new URLSearchParams(typeof window !== 'undefined' ? window.location.search : ''),
        []
    );

    // ── Filter state (URL-synced) ────────────────────────────────────
    const [chartType, setChartType] = useState(() => initialParams.get('chart') || 'bar');
    const [groupBy, setGroupBy] = useState(() => initialParams.get('groupBy') || 'date');
    const [dateRange, setDateRange] = useState(() => {
        const from = initialParams.get('from');
        const to = initialParams.get('to');
        if (from && to) return { from, to };
        return computePreset('last30');
    });
    const [selection, setSelection] = useState(() => {
        const type = initialParams.get('type');
        const ids = initialParams.get('ids');
        if (type && ids) return { type, ids: ids.split(',').filter(Boolean) };
        return { type: null, ids: [] };
    });
    const [metricsFlags, setMetricsFlags] = useState(() => {
        const m = initialParams.get('metrics');
        if (!m) return { ...DEFAULT_METRICS };
        const set = new Set(m.split(','));
        return Object.keys(DEFAULT_METRICS).reduce((acc, k) => {
            acc[k] = set.has(k) ? 1 : 0;
            return acc;
        }, {});
    });
    const [showCompare, setShowCompare] = useState(() => initialParams.get('cmp') === '1');
    const [compareMode, setCompareMode] = useState(() => initialParams.get('cmpMode') || 'prior');
    const [customCompareRange, setCustomCompareRange] = useState(() => {
        const f = initialParams.get('cmpFrom');
        const t = initialParams.get('cmpTo');
        return (f && t) ? { from: f, to: t } : null;
    });

    // Sync filter state to the URL via window.history.replaceState — NOT
    // setSearchParams. Layout.jsx wraps pages in <CSSTransition key=
    // {location.key} unmountOnExit>, so any React-Router navigation re-mounts
    // the page. Re-mount re-runs useState initializers, which re-fires the
    // sync, which re-mounts again — that's an infinite loop. replaceState
    // updates the URL bar without informing the router, so location.key
    // stays the same and the page is not unmounted/remounted.
    const lastUrlRef = useRef(null);
    useEffect(() => {
        const next = new URLSearchParams();
        if (chartType !== 'bar') next.set('chart', chartType);
        if (groupBy !== 'date') next.set('groupBy', groupBy);
        if (dateRange?.from) next.set('from', dateRange.from);
        if (dateRange?.to) next.set('to', dateRange.to);
        if (selection?.type && selection?.ids?.length) {
            next.set('type', selection.type);
            next.set('ids', selection.ids.join(','));
        }
        const activeMetrics = Object.entries(metricsFlags).filter(([, v]) => !!v).map(([k]) => k);
        const defaultActive = Object.keys(DEFAULT_METRICS).filter(k => DEFAULT_METRICS[k]).sort();
        if (activeMetrics.length && JSON.stringify(activeMetrics.slice().sort()) !== JSON.stringify(defaultActive)) {
            next.set('metrics', activeMetrics.join(','));
        }
        if (showCompare) next.set('cmp', '1');
        if (compareMode && compareMode !== 'prior') next.set('cmpMode', compareMode);
        if (compareMode === 'custom' && customCompareRange?.from && customCompareRange?.to) {
            next.set('cmpFrom', customCompareRange.from);
            next.set('cmpTo', customCompareRange.to);
        }
        const qs = next.toString();
        if (qs === lastUrlRef.current) return;
        lastUrlRef.current = qs;
        try {
            const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
            window.history.replaceState(window.history.state, '', url);
        } catch (_) { /* noop in non-browser */ }
    }, [chartType, groupBy, dateRange, selection, metricsFlags, showCompare, compareMode, customCompareRange]);

    // ── Effective selection — fallback to current concessionaire ─────
    const effectiveSelection = useMemo(() => {
        if (selection?.type && selection?.ids?.length) return selection;
        return concessionaireId
            ? { type: 'Concessionaire', ids: [concessionaireId] }
            : { type: null, ids: [] };
    }, [selection, concessionaireId]);

    // ── Effective compare range driven by compareMode ────────────────
    const effectiveCompareRange = useMemo(() => {
        if (compareMode === 'custom') return customCompareRange;
        if (compareMode === 'last_year')  return shiftYearsBack(dateRange, 1);
        if (compareMode === 'last_month') return shiftMonthsBack(dateRange, 1);
        return shiftRangeBackwards(dateRange); // 'prior'
    }, [compareMode, customCompareRange, dateRange]);

    // ── Data ─────────────────────────────────────────────────────────
    const {
        locationsTree, locationsLoading, locationsError,
        metrics, priorMetrics, priorRange,
        current, prior, loading, error,
        refetch, runDrilldown,
    } = useAnalyticsQuery({
        concessionaireId,
        propertyAccess,
        selectedType: effectiveSelection.type,
        selectedIds: effectiveSelection.ids,
        metricsFlags,
        groupBy,
        dateRange,
        compareRange: effectiveCompareRange,
    });

    // ── Drill-down state ─────────────────────────────────────────────
    const [drill, setDrill] = useState({ open: false, loading: false, data: null, error: null });

    const onBucketClick = useCallback(async (idx, row) => {
        setDrill({ open: true, loading: true, data: null, error: null });
        try {
            const date = (groupBy === 'date') ? row.groupByValue : null;
            const data = await runDrilldown({ groupByValue: row.groupByValue, dateOverride: date });
            setDrill({ open: true, loading: false, data, error: null });
        } catch (err) {
            setDrill({ open: true, loading: false, data: null, error: err?.response?.data?.error || err?.message || t('Drill-down failed') });
        }
    }, [groupBy, runDrilldown, t]);

    const closeDrill = useCallback(() => {
        setDrill({ open: false, loading: false, data: null, error: null });
    }, []);

    // ── Toast ────────────────────────────────────────────────────────
    const [toast, setToast] = useState(null);
    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 2500);
    }, []);

    // ── Subtitle ─────────────────────────────────────────────────────
    const subtitle = useMemo(() => {
        const parts = [];
        if (dateRange?.from && dateRange?.to) {
            parts.push(dateRange.from === dateRange.to
                ? dateRange.from
                : `${dateRange.from} → ${dateRange.to}`);
        }
        parts.push(t('grouped by {{groupBy}}', { groupBy }));
        if (effectiveSelection?.type && effectiveSelection?.ids?.length) {
            parts.push(`${effectiveSelection.ids.length} ${effectiveSelection.type}${effectiveSelection.ids.length === 1 ? '' : 's'}`);
        }
        if (showCompare && priorRange?.from && priorRange?.to) {
            parts.push(t('vs {{from}} → {{to}}', { from: priorRange.from, to: priorRange.to }));
        }
        return parts.join(' · ');
    }, [dateRange, groupBy, effectiveSelection, showCompare, priorRange, t]);

    return (
        <div className="v3 v3-anlx-page">
            {/* Header */}
            <div className="v3-anlx-header">
                <div>
                    <h1 className="v3-anlx-title">{t('Analytics')}</h1>
                    <div className="v3-anlx-subtitle">{subtitle}</div>
                </div>
                <div className="v3-anlx-actions">
                    <button
                        type="button"
                        className="v3-btn v3-btn-ghost"
                        onClick={refetch}
                        disabled={loading}
                        aria-label={t('Refresh')}
                    >
                        <RefreshCw size={14} className={loading ? 'spin' : ''} style={{ verticalAlign: -2, marginRight: 4 }} />
                        {loading ? t('Loading…') : t('Refresh')}
                    </button>
                </div>
            </div>

            {/* KPI strip */}
            <AnalyticsKpiStrip current={current} prior={prior} loading={loading && !current} />

            {/* Errors */}
            {locationsError && (
                <div className="v3-card v3-anlx-error" style={{ marginBottom: 12 }}>{locationsError}</div>
            )}
            {error && (
                <div className="v3-card v3-anlx-error" style={{ marginBottom: 12 }}>{error}</div>
            )}

            {/* Body */}
            <div className="v3-anlx-body">
                <AnalyticsFilters
                    tree={locationsTree}
                    treeLoading={locationsLoading}
                    selection={selection}
                    onSelectionChange={setSelection}
                    metricsFlags={metricsFlags}
                    onMetricsChange={setMetricsFlags}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                />

                <div>
                    {/* Chart toolbar */}
                    <div className="v3-anlx-toolbar">
                        <div className="v3-anlx-chart-tabs" role="tablist" aria-label={t('Chart type')}>
                            {CHART_TYPES.map(ct => (
                                <button
                                    key={ct.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={chartType === ct.key}
                                    className={`v3-anlx-chart-tab${chartType === ct.key ? ' is-active' : ''}`}
                                    onClick={() => setChartType(ct.key)}
                                >{t(ct.label)}</button>
                            ))}
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <label className="v3-anlx-groupby">
                                {t('Group by')}
                                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                                    {GROUP_BY_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{t(o.label)}</option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className={`v3-btn ${showCompare ? 'v3-btn-primary' : 'v3-btn-ghost'}`}
                                onClick={() => setShowCompare(v => !v)}
                                title={priorRange ? t('Compare against {{from}} → {{to}}', { from: priorRange.from, to: priorRange.to }) : t('Compare with prior period')}
                                aria-pressed={showCompare}
                            >
                                {showCompare ? t('Compare on') : t('Compare')}
                            </button>
                            {showCompare && (
                                <label className="v3-anlx-groupby" aria-label={t('Compare against')}>
                                    {t('vs')}
                                    <select
                                        value={compareMode}
                                        onChange={(e) => setCompareMode(e.target.value)}
                                    >
                                        <option value="prior">{t('Prior period')}</option>
                                        <option value="last_year">{t('Same period last year')}</option>
                                        <option value="last_month">{t('Same period last month')}</option>
                                        <option value="custom">{t('Custom…')}</option>
                                    </select>
                                </label>
                            )}
                            {showCompare && compareMode === 'custom' && (
                                <span style={{ display: 'inline-flex', gap: 6 }}>
                                    <input
                                        type="date"
                                        className="v3-anlx-date-input"
                                        value={customCompareRange?.from || ''}
                                        max={customCompareRange?.to || undefined}
                                        onChange={(e) => setCustomCompareRange(r => ({ from: e.target.value, to: r?.to || '' }))}
                                        aria-label={t('Compare start date')}
                                    />
                                    <input
                                        type="date"
                                        className="v3-anlx-date-input"
                                        value={customCompareRange?.to || ''}
                                        min={customCompareRange?.from || undefined}
                                        onChange={(e) => setCustomCompareRange(r => ({ from: r?.from || '', to: e.target.value }))}
                                        aria-label={t('Compare end date')}
                                    />
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Chart */}
                    {loading && !metrics?.length ? (
                        <div className="v3-card v3-anlx-chart-shell">
                            <div className="v3-anlx-chart-loading">{t('Loading…')}</div>
                        </div>
                    ) : (
                        <AnalyticsChart
                            chartType={chartType}
                            rows={metrics}
                            compareRows={showCompare ? priorMetrics : null}
                            groupBy={groupBy}
                            metricsFlags={metricsFlags}
                            onBucketClick={onBucketClick}
                        />
                    )}

                    {/* Table */}
                    <AnalyticsTable
                        rows={metrics}
                        compareRows={showCompare ? priorMetrics : null}
                        metricsFlags={metricsFlags}
                        groupBy={groupBy}
                    />
                </div>
            </div>

            {drill.open && (
                <AnalyticsDrilldownPanel
                    data={drill.data}
                    loading={drill.loading}
                    error={drill.error}
                    onClose={closeDrill}
                />
            )}

            {toast && <div className={`v3-anlx-toast ${toast.type}`}>{toast.message}</div>}
        </div>
    );
}
